import { makeAutoObservable, runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import type { ContentLocale } from '@/types/api'
import type en from './locales/en'
import { loadServerMessages } from './serverMessages'
import type { DictionaryPaths, Params, Plural } from './types'

export type Locale = ContentLocale
export type TKey = DictionaryPaths<typeof en>
export type Translate = (key: TKey, params?: Params) => string

/**
 * Each language is its own chunk: only the one in use is downloaded (at boot,
 * or when the user switches). Ўзбекча is derived from O'zbekcha, never
 * maintained by hand.
 */
const dictionaries: Partial<Record<Locale, unknown>> = {}
const loaders: Record<Locale, () => Promise<unknown>> = {
  en: () => import('./locales/en').then((module) => module.default),
  ru: () => import('./locales/ru').then((module) => module.default),
  uz: () => import('./locales/uz').then((module) => module.default),
  cyr: () => Promise.all([import('./locales/uz'), import('./transliterate')]).then(([uz, { transliterateTree }]) => transliterateTree(uz.default)),
}

/**
 * Resolves once the locale's dictionary is in memory; loads it at most once.
 * The backend error catalog follows in the background: nothing waits for it.
 */
export async function loadLocale(locale: Locale) {
  void loadServerMessages(locale)
  if (!dictionaries[locale]) dictionaries[locale] = await loaders[locale]()
}

/** BCP 47 tags for Intl and <html lang>. */
export const LOCALE_TAGS: Record<Locale, string> = { en: 'en-GB', ru: 'ru-RU', uz: 'uz-Latn-UZ', cyr: 'uz-Cyrl-UZ' }
export const LOCALE_NAMES: Record<Locale, string> = { en: 'English', ru: 'Русский', uz: "O'zbekcha", cyr: 'Ўзбекча' }

const STORAGE_KEY = 'mf.ui.locale'
/** Russian is the working language of the business; users can switch any time. */
export const DEFAULT_LOCALE: Locale = 'ru'

function readLocale(): Locale {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    if (stored === 'en' || stored === 'ru' || stored === 'uz' || stored === 'cyr') return stored
  } catch {
    /* storage unavailable */
  }
  return DEFAULT_LOCALE
}

function lookup(dictionary: unknown, key: string): string | Plural | undefined {
  let node: unknown = dictionary
  for (const part of key.split('.')) {
    if (!node || typeof node !== 'object') return undefined
    node = (node as Record<string, unknown>)[part]
  }
  return node as string | Plural | undefined
}

const pluralRules = new Map<Locale, Intl.PluralRules>()

function interpolate(template: string, params?: Params) {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match))
}

export function createTranslator(locale: Locale): Translate {
  const dictionary = dictionaries[locale]
  const rules = pluralRules.get(locale) ?? new Intl.PluralRules(LOCALE_TAGS[locale])
  pluralRules.set(locale, rules)

  return (key, params) => {
    // Every locale matches the English shape (types enforce it), so a miss only falls back when English is loaded.
    const entry = lookup(dictionary, key) ?? lookup(dictionaries.en, key)
    if (entry === undefined) return key
    if (typeof entry === 'string') return interpolate(entry, params)
    const count = Number(params?.count ?? 0)
    const form = rules.select(count) as keyof Plural
    return interpolate(entry[form] ?? entry.other, params)
  }
}

class I18nStore {
  locale: Locale = readLocale()

  constructor() {
    makeAutoObservable(this)
    this.syncDocument()
  }

  get tag() {
    return LOCALE_TAGS[this.locale]
  }

  get t(): Translate {
    return createTranslator(this.locale)
  }

  /** Boots with the stored language; call (and await) before the first render. */
  ready() {
    return loadLocale(this.locale)
  }

  /** Switches once the new dictionary has arrived, so the interface never shows raw keys. */
  async setLocale(locale: Locale) {
    await loadLocale(locale)
    runInAction(() => {
      this.locale = locale
    })
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(locale))
    } catch {
      /* storage unavailable */
    }
    this.syncDocument()
  }

  private syncDocument() {
    document.documentElement.lang = LOCALE_TAGS[this.locale]
  }
}

/** Global instance for code outside React (stores, API errors, formatters). */
export const i18n = new I18nStore()

/** Translate with the current locale outside React. Inside components use useT(). */
export const t: Translate = (key, params) => i18n.t(key, params)

type I18nValue = { t: Translate; locale: Locale; tag: string }
export const I18nContext = createContext<I18nValue | null>(null)

/**
 * Context re-renders every consumer on a locale switch, including components
 * that are not MobX observers — no reload, no lost form state.
 */
export const I18nProvider = observer(function I18nProvider({ children }: { children: ReactNode }) {
  const locale = i18n.locale
  const value = useMemo(() => ({ t: createTranslator(locale), locale, tag: LOCALE_TAGS[locale] }), [locale])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
})

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>')
  return value
}

export function useT(): Translate {
  return useI18n().t
}
